import { CustomButton } from "@/components/ui/Buttons";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import LeftArrowShort from "@/assets/icons/authentication/LeftArrowShort.svg";
import Skip from "@/assets/icons/authentication/Skip.svg";

import InstagramIcon from "@/assets/icons/authentication/linkSocial/Instagram.svg";
import LinkedInIcon from "@/assets/icons/authentication/linkSocial/LinkedIn.svg";
import TiktokIcon from "@/assets/icons/authentication/linkSocial/Tiktok.svg";
import BehanceIcon from "@/assets/icons/authentication/linkSocial/Behaance.svg";

import {
  prevStep,
  nextStep,
  updateV2Field,
} from "@/redux/feature/creatorApplicationV2/creatorApplicationSliceV2";
import { useAppDispatch, useAppSelector } from "@/redux/hooks/hooks";
import ProfileLinkRow from "../../ProfileLinkRow";
import ComfortBlock from "../../ComfortBlock";
import SkillsSection from "./components/SkillsSection";
import PortfolioVideosSection from "./components/PortfolioVideosSection";
import { LanguageSelect } from "@/pages/authentication/signUp/components/ProfessionalDetails";
import ProfileLinksSection, {
  validateProfileLinks,
} from "./components/ProfileLinksSection";
import { SkipToast } from "./Skiptoast";
import { scrollToFirstError } from "@/utils/formValidationV2";
import LanguagesSection, {
  validateLanguages,
} from "./components/LanguagesSection";

type ApplicationStep4Props = {
  currentStep: number;
  setCurrentStep: Dispatch<SetStateAction<number>>;
};

const ApplicationStep4 = ({
  currentStep,
  setCurrentStep,
}: ApplicationStep4Props) => {
  const dispatch = useAppDispatch();
  const [showSkipToast, setShowSkipToast] = useState(false);

  const comfortableWith = useAppSelector(
    (s) => s.signUpV2.v2Extras.comfortableWith,
  );
  const availabilityPerWeek = useAppSelector(
    (s) => s.signUpV2.v2Extras.availableHoursPerWeek,
  );
  const weekendAvailabilityV2 = useAppSelector(
    (s) => s.signUpV2.v2Extras.availabilityForWeekendsOrEvenings,
  );
  const avoidTopics = useAppSelector((s) => s.signUpV2.v2Extras.avoidTopics);

  // -------------------- Comfortable With handlers --------------------
  // ComfortBlock options are strings. We'll store the selected string per key.
  const setComfort = (key: string, selected: string) => {
    dispatch(
      updateV2Field("comfortableWith", {
        ...comfortableWith,
        [key]: selected,
      }),
    );
  };

  // -------------------- Avoid Topics handlers --------------------
  // (Your UI shows "None" only right now; this supports future chips)
  const toggleAvoidTopic = (topic: string) => {
    const exists = avoidTopics.includes(topic);
    const next = exists
      ? avoidTopics.filter((t) => t !== topic)
      : [...avoidTopics, topic];
    dispatch(updateV2Field("avoidTopics", next));
  };

  const profileLinks =
    useAppSelector((s) => s.signUpV2.v2Extras.profileLinks) ?? [];
  const languages = useAppSelector((s) => s.signUpV2.v2Extras.languages) ?? [];

  const [forceValidate, setForceValidate] = useState(false);

  const [topicOtherOpen, setTopicOtherOpen] = useState(false);
  const [topicOtherInput, setTopicOtherInput] = useState("");
  return (
    <>
      <div>
        {/* header */}
        <div className="mb-8">
          <div className="flex items-center gap-x-2 whitespace-nowrap">
            <div className="bg-[#FF6600] px-[8.5px] rounded-[20px]">
              <p className="text-white text-[11px] leading-5 tracking-normal">
                Step 4 of 5
              </p>
            </div>
            <h1 className="text-[#060B13] text-lg md:text-xl tracking-[-0.7px] font-medium">
              Build Your Creator Portfolio
            </h1>
          </div>
          <p className="text-[#475467] text-xs tracking-[-0.5%] mt-2">
            This is the profile brands will see when shortlisting creators. Add
            what shows your experience best.
          </p>
        </div>

        <div className="space-y-8">
          {/* skills */}
          <SkillsSection />

          {/* profile links */}
          <ProfileLinksSection />

          {/* creator add ons and permission */}
          <div>
            <div>
              <h6 className="text-[#121217] text-sm tracking-normal font-medium">
                Creator Add-ons & Permissions
              </h6>
              <p className="text-[#475467] text-[10px] tracking-normal mt-[2px]">
                Some brands may need extra support beyond content creation.
                Select what you’re open to offering.
              </p>
            </div>

            <div className="w-full space-y-2 mt-4">
              <label className="text-xs font-medium text-[#121217] tracking-[-1.5%]">
                What are you comfortable with?
              </label>

              <div className="space-y-3">
                <ComfortBlock
                  title="Post content from your own account"
                  subDescription="Includes organic posts or collab posts."
                  options={["Yes, I can post from my account", "Not available"]}
                  value={comfortableWith.postFromOwnAccount || ""}
                  onChange={(val: string) =>
                    setComfort("postFromOwnAccount", val)
                  }
                />

                <ComfortBlock
                  title="Run ads via your account (Collab / Branded Ads)"
                  subDescription="Only for brand-approved, paid collaborations."
                  options={["Yes, I’m open to running ads", "Not available"]}
                  value={comfortableWith.runAds || ""}
                  onChange={(val: string) => setComfort("runAds", val)}
                />

                <ComfortBlock
                  title="Create a new account for a brand"
                  subDescription="For brands that need a fresh account for campaigns."
                  options={["Yes, I can set up an account", "Not available"]}
                  value={comfortableWith.createBrandAccount || ""}
                  onChange={(val: string) =>
                    setComfort("createBrandAccount", val)
                  }
                />
              </div>
            </div>
          </div>

          {/* upload profile videos */}
          <PortfolioVideosSection />

          <LanguagesSection forceValidate={forceValidate} />

          {/* Availability */}
          <div>
            <p className="tracking-normal text-sm text-[#121217] font-medium">
              Availability
            </p>
            <p className="mt-[2px] text-[10px] tracking-normal">
              This helps us prioritise you for urgent or time-sensitive
              projects.
            </p>

            <div className="bg-[#FAFAFA] py-3 px-2 rounded-lg mt-2">
              <p className="text-[#121217] text-xs tracking-normal">
                Weekly availability
              </p>

              {/* simple example button */}
              <div className="flex flex-wrap mt-2 gap-2">
                {[
                  "1–5 hrs / week",
                  "6–10 hrs / week",
                  "11–20 hrs / week",
                  "20+ hrs / week",
                ].map((label) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() =>
                      dispatch(updateV2Field("availableHoursPerWeek", label))
                    }
                    className={`px-1.5 py-1 border rounded-lg bg-white text-xs tracking-[-0.5px]
                        ${
                          availabilityPerWeek === label
                            ? "border-[#FF6600] bg-[#FFF4EC]"
                            : "border-[#D8D8DD]"
                        }`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="mt-6">
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={weekendAvailabilityV2}
                    onChange={(e) =>
                      dispatch(
                        updateV2Field(
                          "availabilityForWeekendsOrEvenings",
                          e.target.checked,
                        ),
                      )
                    }
                    className="size-3.5 rounded border border-[#D0D5DD] accent-orange-500"
                  />

                  <div className="flex items-center gap-2 text-[10px]">
                    <span className="font-medium text-[#121217]">
                      Flexible timings
                    </span>
                    <span className="text-[#475467]">
                      Open to weekends or evenings?
                    </span>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* avoid topics */}
          <div>
            <p className="tracking-normal text-sm">
              <span className="text-[#121217] font-medium">
                Topics you prefer not to work with
              </span>{" "}
              <span className="text-[#475467]">(optional)</span>
            </p>
            <p className="mt-[2px] text-[10px] tracking-normal">
              This helps us avoid mismatched projects
            </p>

            <div className="flex flex-wrap mt-2 gap-2">
              {["None", "Alcohol", "Gambling", "Adult products"].map((t) => {
                const active =
                  t === "None"
                    ? avoidTopics.length === 0
                    : avoidTopics.includes(t);

                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => {
                      if (t === "None")
                        dispatch(updateV2Field("avoidTopics", []));
                      else toggleAvoidTopic(t);
                    }}
                    className={`px-1.5 py-1 border rounded-lg text-xs tracking-[-0.5px]
                      ${active ? "border-[#FF6600] bg-[#FFF4EC]" : "border-[#D8D8DD]"}`}
                  >
                    {t}
                  </button>
                );
              })}

              {/* Custom topics added by user */}
              {avoidTopics
                .filter(
                  (t) => !["Alcohol", "Gambling", "Adult products"].includes(t),
                )
                .map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => toggleAvoidTopic(t)}
                    className="px-1.5 py-1 border rounded-lg text-xs tracking-[-0.5px] border-[#FF6600] bg-[#FFF4EC]"
                  >
                    {t}
                  </button>
                ))}

              {/* + Other toggle */}
              <button
                type="button"
                onClick={() => setTopicOtherOpen((p) => !p)}
                className={`px-1.5 py-1 border rounded-lg text-xs tracking-[-0.5px] flex items-center gap-1
                  ${topicOtherOpen ? "border-[#FF6600] bg-[#FFF4EC]" : "border-[#D8D8DD]"}`}
              >
                <span className="text-[#FF6600] font-bold">+</span>
                Other
              </button>
            </div>

            {/* Other input row */}
            {topicOtherOpen && (
              <div className="mt-3">
                <div className="flex items-center gap-2">
                  <input
                    value={topicOtherInput}
                    onChange={(e) => setTopicOtherInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        const val = topicOtherInput.trim();
                        if (val) {
                          toggleAvoidTopic(val);
                          setTopicOtherInput("");
                          setTopicOtherOpen(false);
                        }
                      }
                      if (e.key === "Escape") {
                        setTopicOtherOpen(false);
                        setTopicOtherInput("");
                      }
                    }}
                    placeholder="Type a topic (e.g. Politics)"
                    className="flex-1 border border-[#D8D8DD] rounded-xl px-3 py-2 text-sm outline-none focus:border-[#FF6600]"
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const val = topicOtherInput.trim();
                      if (val) {
                        toggleAvoidTopic(val);
                        setTopicOtherInput("");
                        setTopicOtherOpen(false);
                      }
                    }}
                    disabled={!topicOtherInput.trim()}
                    className={`px-4 py-2 rounded-xl text-sm font-medium transition
                      ${
                        topicOtherInput.trim()
                          ? "bg-[#FF6600] text-white hover:opacity-90"
                          : "bg-[#F2F4F7] text-[#98A2B3] cursor-not-allowed"
                      }`}
                  >
                    Add
                  </button>
                </div>
                <p className="text-[10px] text-[#667085] mt-1">
                  Tip: press <span className="font-medium">Enter</span> to add
                  quickly.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* action button */}
        <div className="mt-[50px] flex items-center justify-end gap-3">
          <CustomButton
            variant="outline"
            border="1px solid #E4E4E4"
            fontSize="14px"
            fontWeight="600"
            color="#4D4D4D"
            padding="5px 10.5px"
            borderRadius="200px"
            className="tracking-[-0.11px] flex items-center gap-x-1.5"
            onClick={() => {
              dispatch(prevStep());
              setCurrentStep((prev) => prev - 1);
            }}
          >
            <LeftArrowShort />
            Go Back
          </CustomButton>

          <CustomButton
            variant="outline"
            border="1px solid #E4E4E4"
            fontSize="14px"
            fontWeight="600"
            color="#4D4D4D"
            padding="5px 10.5px"
            borderRadius="200px"
            className="tracking-[-0.11px] flex items-center gap-x-1.5"
            onClick={() => {
              setShowSkipToast(true);
              setTimeout(() => {
                dispatch(nextStep());
                setCurrentStep((prev) => prev + 1);
              }, 2000);
            }}
          >
            <Skip />
            Skip
          </CustomButton>

          <CustomButton
            backgroundColor="#FF6600"
            fontSize="14px"
            fontWeight="600"
            color="white"
            padding="5px 10.5px"
            borderRadius="200px"
            className="tracking-[-0.11px]"
            onClick={() => {
              setForceValidate(true);
              const errors = {
                ...validateProfileLinks(profileLinks),
                ...validateLanguages(languages),
              };
              if (Object.keys(errors).length > 0) {
                scrollToFirstError(errors);
                return;
              }
              dispatch(nextStep());
              setCurrentStep((prev) => prev + 1);
            }}
          >
            Proceed
          </CustomButton>
        </div>
      </div>
      <SkipToast
        visible={showSkipToast}
        onClose={() => setShowSkipToast(false)}
      />
    </>
  );
};

export default ApplicationStep4;
